import { formatKarachiDateTime } from '@/lib/dates'
import { formatPKR } from '@/lib/money'
import type { ReceiptInfo } from './shareText'

/**
 * Render the receipt as a clean white "paper" PNG (proper template, not plain chat text).
 * Drawn on a canvas so it can be shared as an image via the Web Share sheet / saved.
 */
export async function renderReceiptPng(r: ReceiptInfo): Promise<Blob> {
  const W = 800
  const pad = 56
  const lineH = 44
  const imeiLines = r.imeis.length > 0 ? r.imeis : []
  const rows = 4 + imeiLines.length + 3 // item, imei(s), customer, date + amount, received, balance
  const H = 260 + rows * lineH + 120

  const canvas = document.createElement('canvas')
  canvas.width = W
  canvas.height = H
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas unavailable')

  // Paper
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, W, H)

  let y = 90
  ctx.fillStyle = '#0f172a'
  ctx.font = '700 40px system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText(r.shopName?.trim() ? r.shopName.trim() : 'MOBILE TRADING PRO', W / 2, y)
  y += 40
  ctx.fillStyle = '#64748b'
  ctx.font = '400 24px system-ui, sans-serif'
  ctx.fillText('SALES RECEIPT', W / 2, y)
  y += 46

  // Divider
  ctx.strokeStyle = '#0f172a'
  ctx.lineWidth = 3
  ctx.setLineDash([10, 8])
  ctx.beginPath()
  ctx.moveTo(pad, y)
  ctx.lineTo(W - pad, y)
  ctx.stroke()
  ctx.setLineDash([])
  y += 46

  const label = (t: string) => {
    ctx.fillStyle = '#64748b'
    ctx.font = '400 26px system-ui, sans-serif'
    ctx.textAlign = 'left'
    ctx.fillText(t, pad, y)
  }
  const value = (t: string, strong = false, color = '#0f172a') => {
    ctx.fillStyle = color
    ctx.font = `${strong ? '700' : '400'} 26px system-ui, sans-serif`
    ctx.textAlign = 'right'
    ctx.fillText(t, W - pad, y)
  }
  const row = (l: string, v: string, strong = false, color = '#0f172a') => {
    label(l)
    value(v, strong, color)
    y += lineH
  }

  ctx.textAlign = 'left'
  row('Item', r.model, true)
  imeiLines.forEach((im, i) => row(`IMEI ${i + 1}`, im))
  row('Customer', r.customer?.trim() ? r.customer.trim() : 'Walk-in')
  row('Date', formatKarachiDateTime(r.soldAt))
  y += 12
  row('Amount', formatPKR(r.saleAmount), true)
  row('Received', formatPKR(r.received))
  if (r.balance > 0) row('Balance (udhaar)', formatPKR(r.balance), true, '#dc2626')
  else row('Balance', 'Paid in full', true, '#16a34a')

  y += 24
  ctx.strokeStyle = '#0f172a'
  ctx.setLineDash([10, 8])
  ctx.beginPath()
  ctx.moveTo(pad, y)
  ctx.lineTo(W - pad, y)
  ctx.stroke()
  ctx.setLineDash([])
  y += 52
  ctx.fillStyle = '#64748b'
  ctx.font = '400 24px system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.fillText('Shukriya! Please keep this receipt for warranty / return reference.', W / 2, y)

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png')
  })
}
